import { ApolloClient, ApolloLink, InMemoryCache, Operation } from '@apollo/client';
import { onError, ErrorResponse } from '@apollo/client/link/error';
import { ErrorHandler } from '@sixfold/app-utils';
import { AggregateError, notNil } from '@sixfold/typed-primitives';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';

import { responseSessionAction, SessionFailureAction } from '../authorization/session_state';
import { underscore, pascalToCamelCase } from '../util/string';

export function dataIdFromObject(object: Record<string, unknown>): string {
  const typename = object.__typename as string;
  const snakeCaseTypename = underscore(typename);
  const snakeCaseIdFieldName = `${snakeCaseTypename}_id`;

  const camelCaseTypename = pascalToCamelCase(typename);
  const camelCaseIdFieldName = `${camelCaseTypename}Id`;

  if (object[camelCaseIdFieldName] !== undefined) {
    return `${snakeCaseTypename}:${object[camelCaseIdFieldName]}`;
  }

  if (object[snakeCaseIdFieldName] !== undefined) {
    return `${snakeCaseTypename}:${object[snakeCaseIdFieldName]}`;
  }

  return object.id as string;
}

interface ServerErrorLike extends Error {
  statusCode: number;
}

export const createApolloClient = ({
  handleError,
  onAuthenticationError,
  onGraphQLError,
}: {
  onAuthenticationError: (action: SessionFailureAction) => void;
  onGraphQLError: (aggregatedError: string) => void;
  handleError: ErrorHandler | undefined;
}) => {
  const uploadLink = createUploadLink({ uri: '/graphql', headers: { 'apollo-require-preflight': 'true' } });

  const link = ApolloLink.from([
    onError(({ operation, graphQLErrors, networkError }: ErrorResponse) => {
      const statusCode = networkError != null && isServerError(networkError) ? networkError.statusCode : undefined;
      const sessionAction = responseSessionAction({
        operationName: operation.operationName,
        statusCode,
        graphQLErrors,
      });

      if (sessionAction !== undefined) {
        onAuthenticationError(sessionAction);
        return;
      }

      reportNetworkError(operation, networkError ?? undefined, handleError);
      reportGraphQLErrors(operation, graphQLErrors, handleError);

      const errors = [...(graphQLErrors ?? []).map((e) => new Error(e.message)), networkError].filter(notNil);
      const aggregatedError = new AggregateError(errors);

      onGraphQLError(aggregatedError.message);
    }),
    uploadLink,
  ]);

  return new ApolloClient({
    link,
    cache: new InMemoryCache({
      dataIdFromObject,
      possibleTypes: {
        Geofence: ['GeofenceCircle', 'GeofencePolygon'],
        CombinedEvent: ['StopEvent', 'TourEvent'],
        ActionMetadata: ['FlagCreatedMetadata', 'FlagUpdatedMetadata'],
      },
    }),
  });
};

function reportGraphQLErrors(
  operation: Operation,
  errors: ErrorResponse['graphQLErrors'],
  handleError: ErrorHandler | undefined,
) {
  if (errors === undefined || errors.length === 0) {
    return;
  }

  const aggregatedError = new AggregateError(errors.map((e) => new Error(e.message)));
  console.error('GraphQL error', aggregatedError);

  handleError?.captureError(aggregatedError, {
    extra: {
      errors: errors.map(({ message, locations }) => ({
        message,
        operationName: operation.operationName,
        variables: operation.variables,
        extensions: operation.extensions,
        locations: locations !== undefined ? locations.map(({ line, column }) => ({ line, column })) : undefined,
      })),
    },
  });
}

function reportNetworkError(operation: Operation, error: Error | undefined, handleError: ErrorHandler | undefined) {
  if (error === undefined) {
    return;
  }

  const { response } = operation.getContext();
  if (response?.status === 401) {
    return;
  }

  handleError?.captureError(error, {
    extra: {
      operationName: operation.operationName,
      variables: operation.variables,
      extensions: operation.extensions,
    },
  });
}

function isServerError(error: Error): error is ServerErrorLike {
  return 'statusCode' in error;
}
